import React from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Plus, Heart } from "lucide-react";
import { toast } from "sonner";
import { Button } from "./ui/button";
import { Badge } from "./ui/badge";
import { useCart } from "../lib/cart-context";
import { useAuth } from "../lib/auth-context";
import { useFavorites } from "../lib/favorites-context";
import { useShopHref } from "../lib/shop-context";
import { imageUrl, formatEUR } from "../lib/api";

export default function ProductCard({ product }) {
  const { addItem } = useCart();
  const { user } = useAuth();
  const { isFavorite, toggle } = useFavorites();
  const favorited = user && isFavorite(product.id);
  const navigate = useNavigate();
  const location = useLocation();
  const shopHref = useShopHref();
  const productHref = shopHref(`/producto/${product.slug}`);

  const onAdd = (e) => {
    e.preventDefault();
    // Phase 39b fix — guest cart is fully supported by the backend via the
    // `X-Session-Id` header (already injected by `api.js` interceptor).
    // No login gate here: users only need to authenticate at checkout.
    addItem(product, 1);
  };

  return (
    <div
      data-testid={`product-card-${product.slug}`}
      className="group relative flex flex-col rounded-xl border border-neutral-200 bg-white overflow-hidden hover:shadow-md hover:border-neutral-300 transition-all"
    >
      <Link
        to={productHref}
        className="relative aspect-square bg-neutral-50 grid place-items-center overflow-hidden"
      >
        {product.is_new && (
          <Badge
            data-testid={`badge-nuevo-${product.slug}`}
            className="absolute top-3 left-3 z-10 bg-[#00953B] hover:bg-[#00953B] text-white rounded-full px-3"
          >
            Nuevo
          </Badge>
        )}
        <img
          src={imageUrl(product.image_url)}
          alt={product.name}
          className="h-full w-full object-contain p-4 group-hover:scale-[1.03] transition-transform"
          loading="lazy"
        />
      </Link>

      {user && (
        <button
          data-testid={`fav-toggle-${product.slug}`}
          aria-label={favorited ? "Quitar de habituales" : "Añadir a habituales"}
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            toggle(product);
          }}
          className={`absolute top-3 right-3 z-10 h-9 w-9 rounded-full grid place-items-center backdrop-blur bg-white/90 border border-neutral-200 hover:border-[#00953B] transition-colors ${
            favorited ? "text-[#00953B]" : "text-neutral-400"
          }`}
        >
          <Heart className={`h-4 w-4 ${favorited ? "fill-current" : ""}`} />
        </button>
      )}

      <div className="flex flex-col flex-1 p-3 gap-1">
        <div className="text-[11px] uppercase tracking-wide text-neutral-500">
          {product.brand}
        </div>
        <Link
          to={productHref}
          className="text-sm font-medium leading-snug line-clamp-2 min-h-[2.6rem] hover:underline"
        >
          {product.name}
        </Link>
        <div className="mt-1 text-[11px] text-neutral-500">
          {product.unit_label} · {product.price_per_unit}
        </div>
        {(() => {
          // Phase 45 — for per-unit ("ud") products we compute a rough
          // weight and surface the equivalent €/kg so shoppers can still
          // compare against per-weight products. We hide it entirely for
          // rows where the weight is the coarse fallback (would mislead).
          const ppu = product.price_per_unit || "";
          const isPerUnit = /\/ud|\/unidad/i.test(ppu);
          const src = product.weight_kg_source;
          const w = Number(product.weight_kg || 0);
          if (!isPerUnit || !w || src === "fallback") return null;
          const eurPerKg = product.price_eur / w;
          if (!Number.isFinite(eurPerKg) || eurPerKg <= 0) return null;
          const formatted = eurPerKg.toLocaleString("es-ES", {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
          });
          return (
            <div
              data-testid={`product-eur-per-kg-${product.slug}`}
              className="text-[11px] text-neutral-400"
            >
              ~{formatted} €/kg
            </div>
          );
        })()}
        <div className="mt-auto flex items-end justify-between pt-2">
          <div className="text-lg font-black">{formatEUR(product.price_eur)}</div>
          <Button
            data-testid={`add-to-cart-${product.slug}`}
            onClick={onAdd}
            className="h-9 rounded-full bg-[#00953B] hover:bg-[#007a30] text-white gap-1 px-4"
          >
            <Plus className="h-4 w-4" />
            Añadir
          </Button>
        </div>
      </div>
    </div>
  );
}
